// stands.ts — Stands and finished products (book REGLES_DEVIS §9).
//
// Explicit quantity tiers 1+/5+/10+, never the standard m² tiers. Quantities accumulate BY MAJOR
// FAMILY through the reference prefix (PAR umbrella, PHO photocall, ROL rollup…): all variants add
// up to set the tier LEVEL, while each product keeps its own pv_tier1/pv_tier2/pv_tier3 and pa_tier1/pa_tier2/pa_tier3.
// The client discount applies on top of the tier for transfers (PV × (1−clientRate), PA × (1−clientRate×2/3), capped
// at 60 %); a STAND takes no agency discount (the caller passes 0).

import { PA_SLOPE, STAND_SUBPRODUCTS, CUSTOM_SIZE_RATES } from "../../data";
import type { Stand, CustomSizeRate } from "../../models/data.model";
import type { StandSubproducts } from "../../models/stands.model";
import { type QuoteLine, num, round2, isFilled, findStand } from "../core/core";
import { isTierRegime } from "../core/regimes";

/** Labels of the major families (ref prefix) of the tiered regimes. */
const STAND_FAMILY_LABEL: Record<string, string> = {
  PAR: "Stand parapluie",
  PHO: "Photocall",
  ROL: "Rollup",
  ORI: "Oriflamme",
  ACC: "Accessoire stand",
  TRS: "Transfert mini", // family of the TRANSFER regime (S2)
};

/**
 * Major family of a product = its reference prefix (before the first dash).
 * @param ref - Product reference.
 * @returns The family prefix (empty when absent).
 */
export function standFamily(ref: string | undefined): string {
  return String(ref ?? "").split("-")[0] ?? "";
}

/**
 * Family label (raw prefix when unknown).
 * @param family - Family prefix.
 * @returns The label, or the raw prefix when unknown.
 */
export function familyLabel(family: string): string {
  return STAND_FAMILY_LABEL[family] ?? family;
}

/**
 * Cumulated quantity per major family, over the stand / finished-product lines.
 * @param lines - All quote lines.
 * @returns Quantity per family prefix.
 */
export function cumulateByFamily(lines: readonly QuoteLine[]): Map<string, number> {
  const cumulated = new Map<string, number>();
  for (const line of lines) {
    if (isTierRegime(line) && line.ref && findStand(line.ref)) {
      const family = standFamily(line.ref);
      cumulated.set(family, (cumulated.get(family) ?? 0) + num(line.fileQty) * num(line.copiesPerFile));
    }
  }
  return cumulated;
}

/**
 * Tier unit sale price by cumulated family quantity (≥10 → pv_tier3, ≥5 → pv_tier2, otherwise pv_tier1).
 * @param stand - Stand or finished product.
 * @param cumulatedQuantity - Cumulated quantity of its family.
 * @returns The tier unit sale price.
 */
export function tierUnitPrice(stand: Stand, cumulatedQuantity: number): number {
  if (cumulatedQuantity >= 10) return num(stand.pv_tier3);
  if (cumulatedQuantity >= 5) return num(stand.pv_tier2);
  return num(stand.pv_tier1);
}

/**
 * Matching tier purchase price (same 1/5/10 logic, used for the margin).
 * @param stand - Stand or finished product.
 * @param cumulatedQuantity - Cumulated quantity of its family.
 * @returns The tier unit purchase price.
 */
export function tierPa(stand: Stand, cumulatedQuantity: number): number {
  if (cumulatedQuantity >= 10) return num(stand.pa_tier3);
  if (cumulatedQuantity >= 5) return num(stand.pa_tier2);
  return num(stand.pa_tier1);
}

/**
 * DEFAULT composition of a COMPLETE stand as priced sub-products (structure + fabric), or `[]` when
 * the product is not a composable complete one (sub-product, accessory, unknown ref).
 * @param ref - Product reference.
 * @returns The default sub-product references, or `[]`.
 */
export function defaultComposition(ref: string | undefined): string[] {
  const entry = STAND_SUBPRODUCTS[String(ref ?? "")];
  return entry ? [entry.structure, entry.fabric] : [];
}

/**
 * Does a stand line composition differ from the DEFAULT composition of its complete product?
 * Detected BY COMPARISON, never by a flag: missing composition OR equal to the default → false (PACK
 * regime); different → true (COMPOSED regime). A non-composable product (empty default: sub-product
 * sold alone, ACC) is always PACK.
 * @param line - Stand line.
 * @returns True when the composition differs from the default (composed regime).
 */
export function isCompositionModified(line: QuoteLine): boolean {
  const defaultComp = defaultComposition(line.ref);
  if (defaultComp.length === 0) return false; // not a composable complete product → always pack
  if (line.composition == null) return false; // implicit default composition → pack
  const a = [...line.composition].map(String).sort();
  const b = [...defaultComp].sort();
  return a.length !== b.length || a.some((x, i) => x !== b[i]);
}

/** Retained price of a stand line (before rounding/total). */
export interface StandPrice {
  /** Unit sale price (tier, client-discounted, or forced). */
  pu: number;
  /** Unit purchase price (tier, eroded by the slope applied to the client discount). */
  pa: number;
  /** Effective client discount applied (0 when the price is forced). */
  totalDiscount: number;
  /** Price regime: pack is the stored bundle price of the complete product (default composition);
   *  the composed regime is the plain sum of the effective sub-products, which loses the pack
   *  discount. Exposed for the badge and the export. */
  regime: "pack" | "composed";
}

/**
 * Price of a stand line. Base = tier unit sale/purchase price of the family:
 * - PACK regime (default composition) → stored bundle price of the complete product, UNCHANGED;
 * - COMPOSED regime (modified composition) → plain Σ of the effective sub-products, each priced at
 *   the FAMILY tier (cumulated quantity unchanged); the Σ loses the pack discount, which rewards the standard pack.
 * Then the client discount on top (PV × (1−clientRate), PA × (1−clientRate×PA_SLOPE), capped at 60 %). A forced
 * price wins.
 * @param line - Stand line.
 * @param stand - Catalogue stand of the line.
 * @param cumulatedQuantity - Cumulated quantity of its family.
 * @param clientDiscount - Client discount rate (0..1).
 * @returns The retained unit prices, discount and regime.
 */
export function standPrice(line: QuoteLine, stand: Stand, cumulatedQuantity: number, clientDiscount: number): StandPrice {
  const isComposed = isCompositionModified(line);
  let pu: number;
  let pa: number;
  if (isComposed) {
    pu = 0;
    pa = 0;
    for (const ref of line.composition ?? []) {
      const subProduct = findStand(ref);
      if (!subProduct) continue; // unknown sub-product → ignored (guard; the UI only offers valid refs)
      pu += tierUnitPrice(subProduct, cumulatedQuantity);
      pa += tierPa(subProduct, cumulatedQuantity);
    }
  } else {
    pu = tierUnitPrice(stand, cumulatedQuantity);
    pa = tierPa(stand, cumulatedQuantity);
  }
  let totalDiscount = 0;
  if (isFilled(line.forcedPv)) {
    pu = num(line.forcedPv);
  } else {
    const clientRate = Math.min(0.6, clientDiscount || 0);
    if (clientRate > 0) {
      totalDiscount = clientRate;
      pu = pu * (1 - clientRate);
      pa = pa * (1 - clientRate * PA_SLOPE);
    }
  }
  return { pu, pa, totalDiscount: totalDiscount, regime: isComposed ? "composed" : "pack" };
}

/**
 * DEVELOPED visual area (m²) of a stand × quantity — purely informative (rounded to 3 decimals).
 *
 * `H` holds the NOMINAL height of ONE face everywhere; double-sided is carried by `faces`. The
 * doubling factor only applies to components that carry printing: a STRUCTURE is a frame whose area
 * is physical and does not double, whereas a printed fabric does.
 * @param stand - Stand or finished product.
 * @param quantity - Number of pieces.
 * @returns The developed visual area in m².
 */
export function standArea(stand: Stand, quantity: number): number {
  const isPrinted = stand.component !== "STRUCTURE"; // COMPLET and TOILE carry the artwork; the frame does not.
  const faces = isPrinted ? (stand.faces ?? 1) : 1;
  return stand.L && stand.H ? Math.round((stand.L * stand.H * faces / 1e6) * quantity * 1000) / 1000 : 0;
}

/**
 * PRINTED area (m²) of a stand × quantity, the surface to produce: every printed face of a COMPLET or TOILE, nothing
 * for a bare STRUCTURE, whether its component says so or the sub-products table lists it as the structure of a
 * complete stand. A custom-size photocall is produced at its final format, not the catalogue one.
 * @param stand - Stand or finished product.
 * @param quantity - Number of pieces.
 * @param size - Final format of one face in mm (custom size), when it replaces the catalogue one.
 * @param subproducts - Structure and fabric of each complete stand.
 * @returns The printed area in m², rounded to 3 decimals.
 */
export function standPrintedM2(
  stand: Stand,
  quantity: number,
  size?: { L: number; H: number },
  subproducts: Readonly<Record<string, StandSubproducts>> = STAND_SUBPRODUCTS,
): number {
  if (stand.component === "STRUCTURE") return 0;
  if (Object.values(subproducts).some((s) => s.structure === stand.ref)) return 0;
  const L = size?.L ?? stand.L;
  const H = size?.H ?? stand.H;
  return L && H ? Math.round(((L * H * (stand.faces ?? 1)) / 1e6) * quantity * 1000) / 1000 : 0;
}

/**
 * PRINTED area (m²) of a stand line, by its effective composition: the stand itself in its default composition, the
 * sum of its sub-products when the composition was changed (a structure-only composition prints nothing).
 * @param line - Stand line.
 * @param stand - Catalogue stand of the line.
 * @param quantity - Number of pieces.
 * @param size - Final format of one face in mm (custom size), when it replaces the catalogue one.
 * @param lookup - Catalogue lookup of a sub-product.
 * @param subproducts - Structure and fabric of each complete stand.
 * @returns The printed area in m², rounded to 3 decimals.
 */
export function standLinePrintedM2(
  line: QuoteLine,
  stand: Stand,
  quantity: number,
  size?: { L: number; H: number },
  lookup: (ref: string) => Stand | undefined = findStand,
  subproducts: Readonly<Record<string, StandSubproducts>> = STAND_SUBPRODUCTS,
): number {
  // Same test as isCompositionModified, against the given sub-products table.
  const entry = subproducts[String(line.ref ?? "")];
  const def = entry ? [entry.structure, entry.fabric].sort() : [];
  const comp = line.composition ? [...line.composition].map(String).sort() : null;
  const modified = def.length > 0 && comp != null && (comp.length !== def.length || comp.some((x, i) => x !== def[i]));
  if (!modified) return standPrintedM2(stand, quantity, size, subproducts);
  let m2 = 0;
  for (const ref of comp!) {
    const sub = lookup(ref);
    if (sub) m2 += standPrintedM2(sub, quantity, size, subproducts);
  }
  return Math.round(m2 * 1000) / 1000;
}

// CUSTOM-SIZE mode (PHO family)
//
// Custom size does not create a product: it charges the area DIFFERENCE between the requested format
// and the catalogue reference it starts from. The rate is derived from the PARENT — never picked by
// the sales rep — from three data-borne attributes: family, component and number of faces.

/**
 * Extra rate applicable to a parent line. `null` when custom size is not open for that
 * combination.
 *
 * The lookup uses (family, component, faces), NEVER the parent reference: that is what lets the 42
 * photocalls share six rates, and PAR/ROL open up without touching the code.
 * @param family - Family prefix of the parent.
 * @param component - Component of the parent.
 * @param faces - Number of faces of the parent.
 * @returns The rate, or `null` when custom size is not open.
 */
export function customSizeRate(family: string, component: string | undefined, faces: number | undefined): CustomSizeRate | null {
  if (!family || !component || !faces) return null;
  return CUSTOM_SIZE_RATES.find(
    (t) => t.family === family && t.component === component && t.faces === faces,
  ) ?? null;
}

/**
 * Rate looked up by its REFERENCE. Necessary counterpart of `customSizeRate`: an ALREADY priced line
 * only carries the ref of the retained rate, and that is how read-backs (export, client wording) get
 * to its label and €/m² price WITHOUT redoing the resolution that produced the price.
 * @param ref - Reference of the rate.
 * @returns The rate, or `null`.
 */
export function customSizeRateByRef(ref: string | null | undefined): CustomSizeRate | null {
  return ref ? CUSTOM_SIZE_RATES.find((t) => t.ref === ref) ?? null : null;
}

/**
 * Rate applicable to a STAND parent line, derived from its catalogue attributes.
 * @param stand - Parent stand.
 * @returns The rate, or `null`.
 */
export function customSizeRateForStand(stand: Stand): CustomSizeRate | null {
  return customSizeRate(standFamily(stand.ref), stand.component, stand.faces);
}

// TRANSFER regime (TRS family, S2) — OWN tiers 6/11 + RIGID purchase price
// Reuses the per-family accumulation machinery (cumulateByFamily via isTierRegime), but with its
// thresholds written out here (regime rules, never parameterised) and a purchase price insensitive to
// both the tier and the client discount. Columns pv_tier1/pv_tier2/pv_tier3 = tiers 1/2/3.

/**
 * TRANSFER tier unit sale price by cumulated TRS quantity (≥11 → tier 3, ≥6 → tier 2, else tier 1).
 * @param stand - Transfer product.
 * @param cumulatedQuantity - Cumulated TRS quantity.
 * @returns The tier unit sale price.
 */
export function transferTierUnitPrice(stand: Stand, cumulatedQuantity: number): number {
  if (cumulatedQuantity >= 11) return num(stand.pv_tier3); // tier 3 (−10 %)
  if (cumulatedQuantity >= 6) return num(stand.pv_tier2); // tier 2 (−5 %)
  return num(stand.pv_tier1); // tier 1 (full price)
}

/**
 * Price of a TRANSFER line. Unit price = TRANSFER tier (thresholds 6/11), client discount ON TOP as
 * PV × (1−clientRate) (capped at 60 %); a forced sale price wins and cancels the discount. The purchase price
 * is RIGID: identical across the three tiers AND never eroded by the client discount, so the margin
 * absorbs it alone. There is no composed regime, since a sheet has no sub-products.
 * @param line - Transfer line.
 * @param stand - Catalogue product of the line.
 * @param cumulatedQuantity - Cumulated TRS quantity.
 * @param clientDiscount - Client discount rate (0..1).
 * @returns The retained unit prices, discount and regime.
 */
export function transferPrice(line: QuoteLine, stand: Stand, cumulatedQuantity: number, clientDiscount: number): StandPrice {
  let pu = transferTierUnitPrice(stand, cumulatedQuantity);
  const pa = num(stand.pa_tier1); // rigid purchase price (pa_tier1 = pa_tier2 = pa_tier3 in the data), tier- and discount-proof
  let totalDiscount = 0;
  if (isFilled(line.forcedPv)) {
    pu = num(line.forcedPv);
  } else {
    const clientRate = Math.min(0.6, clientDiscount || 0);
    if (clientRate > 0) {
      totalDiscount = clientRate;
      pu = pu * (1 - clientRate); // purchase price NOT eroded: no slope applied to it
    }
  }
  return { pu, pa, totalDiscount: totalDiscount, regime: "pack" };
}
